import { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Bell, BellOff, CheckCircle2, XCircle, AlertTriangle, Send } from "lucide-react";
import { apiCall } from "../admin/calendar/calendarUtils";
import { pushSupported, isIOS, isStandalone, iosVersion, permissionState, currentSubscription, checkRegistered, enablePush, disablePush } from "./push";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" }) : "–");

const Row = ({ ok, warn, label, hint, testId }) => (
  <li className="flex gap-2 text-xs" data-testid={testId}>
    {ok ? <CheckCircle2 className="w-4 h-4 text-[#c1ff72] shrink-0" /> : warn ? <AlertTriangle className="w-4 h-4 text-orange-300 shrink-0" /> : <XCircle className="w-4 h-4 text-red-400 shrink-0" />}
    <span><span className="text-white">{label}</span>{hint && <span className="block text-[#A1A1AA]">{hint}</span>}</span>
  </li>
);

function Checklist({ s }) {
  const ios = isIOS();
  const v = iosVersion();
  return (
    <ul className="space-y-2" data-testid="push-checklist">
      <Row ok={window.isSecureContext} label="Sichere Verbindung (HTTPS)" hint={!window.isSecureContext && "Bitte die App über https:// öffnen."} testId="check-https" />
      {ios && <Row ok={!v || v >= 16.4} label={`iOS-Version ${v ? v.toFixed(2).replace(/0$/, "") : "unbekannt"}`} hint={v && v < 16.4 && "Push benötigt mindestens iOS 16.4 – bitte iPhone aktualisieren."} testId="check-ios-version" />}
      <Row ok={isStandalone()} warn={!ios} label="Vom Home-Bildschirm geöffnet" hint={!isStandalone() && (ios ? "Auf dem iPhone funktioniert Push nur in der installierten App (siehe Anleitung)." : "Empfohlen: App installieren.")} testId="check-standalone" />
      <Row ok={pushSupported()} label="Push wird vom Gerät unterstützt" hint={!pushSupported() && "In diesem Browser/Modus nicht verfügbar."} testId="check-supported" />
      <Row ok={s.perm === "granted"} warn={s.perm === "default"} label={`Mitteilungen: ${{ granted: "erlaubt", denied: "abgelehnt", default: "noch nicht gefragt", unsupported: "nicht verfügbar" }[s.perm]}`} testId="check-permission" />
      <Row ok={s.serverReady} label="Server bereit (VAPID-Schlüssel vorhanden)" testId="check-server" />
      <Row ok={s.registered} label={s.registered ? "Dieses Gerät ist registriert" : "Dieses Gerät ist nicht registriert"} testId="check-registered" />
    </ul>
  );
}

const Guide = ({ denied }) => (
  <div className="text-xs text-[#A1A1AA] space-y-2 border-t border-white/10 pt-3" data-testid="push-guide">
    {denied ? (
      <p className="text-orange-300" data-testid="push-denied-help"><b>Mitteilungen wurden abgelehnt.</b> iPhone: Einstellungen → Mitteilungen → „PFT Admin“ → „Mitteilungen erlauben“ einschalten (auch „Sperrbildschirm“ und „Banner“). Danach die App schließen, neu öffnen und hier erneut aktivieren. Falls „PFT Admin“ nicht erscheint: App vom Home-Bildschirm löschen und neu hinzufügen.</p>
    ) : null}
    <p className="text-white font-semibold">Anleitung iPhone (ab iOS 16.4)</p>
    <ol className="list-decimal pl-4 space-y-1">
      <li>In <b>Safari</b> öffnen: personal-fussballtraining.de/app</li>
      <li>Teilen-Symbol (Quadrat mit Pfeil) → „Zum Home-Bildschirm“ → Hinzufügen.</li>
      <li>Safari schließen und <b>„PFT Admin“ vom Home-Bildschirm</b> starten, anmelden.</li>
      <li>Mehr → Push-Benachrichtigungen → „Aktivieren“ → „Erlauben“ tippen.</li>
      <li>„Test-Push an mein iPhone senden“ tippen und iPhone sperren – die Nachricht sollte erscheinen.</li>
    </ol>
    <p>Hinweis: Im Fokus-/Nicht-stören-Modus kann iOS Mitteilungen zurückhalten.</p>
  </div>
);

function Devices({ list }) {
  if (!list.length) return <p className="text-xs text-[#71717A]" data-testid="push-devices-empty">Noch kein Gerät registriert.</p>;
  return (
    <ul className="space-y-1" data-testid="push-devices">
      {list.map((d, i) => (
        <li key={i} className="text-xs text-[#A1A1AA]" data-testid={`push-device-${i}`}>
          <span className="text-white">{d.device_label}</span> · registriert {fmt(d.updated_at)} · letzte Übergabe {fmt(d.last_success_at)}
          {d.last_error && <span className="block text-red-300">Letzter Fehler: {d.last_error}</span>}
        </li>
      ))}
    </ul>
  );
}

function TestResult({ r }) {
  if (!r) return null;
  if (!r.devices) return <p className="text-xs text-red-300" data-testid="push-test-result">Kein registriertes Gerät – bitte zuerst aktivieren.</p>;
  return (
    <div className="text-xs space-y-1" data-testid="push-test-result">
      {r.results.map((x, i) => (
        <p key={i} className={x.ok ? "text-[#c1ff72]" : "text-red-300"}>
          {x.device}: {x.ok ? `an den Push-Dienst übergeben (Status ${x.status_code})` : x.error}
        </p>
      ))}
      <p className="text-[#71717A]">„Übergeben“ heißt: Apple/Google hat die Nachricht angenommen. Ob sie auf dem Gerät angezeigt wurde, kann der Server technisch nicht prüfen – bitte am iPhone kontrollieren.</p>
    </div>
  );
}

export function PushSettings({ token }) {
  const [s, setS] = useState({ sub: null, registered: false, perm: permissionState(), serverReady: false, devices: [] });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const refresh = useCallback(async () => {
    const sub = await currentSubscription().catch(() => null);
    const [st, registered] = await Promise.all([apiCall(token, "/app/admin/push/status").catch(() => ({ devices: [] })), checkRegistered(token, sub).catch(() => false)]);
    setS({ sub, registered, perm: permissionState(), serverReady: !!st.server_ready, devices: st.devices || [] });
  }, [token]);
  useEffect(() => { refresh(); }, [refresh]);
  const toggle = async () => {
    setBusy(true);
    try {
      if (s.registered) { await disablePush(token); toast.success("Push-Benachrichtigungen deaktiviert"); }
      else { await enablePush(token); toast.success("Dieses Gerät ist jetzt registriert"); }
    } catch (e) { toast.error(e.message); }
    setResult(null); await refresh(); setBusy(false);
  };
  const test = async () => {
    setBusy(true);
    try { setResult(await apiCall(token, "/app/admin/push/test", { method: "POST" })); } catch (e) { toast.error(e.message); }
    await refresh(); setBusy(false);
  };
  return (
    <div className="bg-[#121212] border border-white/10 p-4 space-y-4" data-testid="push-card">
      <div className="flex items-center gap-2 text-white font-semibold">{s.registered ? <Bell className="w-5 h-5 text-[#c1ff72]" /> : <BellOff className="w-5 h-5 text-[#71717A]" />}Push-Benachrichtigungen
        <span className={`ml-auto text-xs px-2 py-0.5 ${s.registered ? "bg-[#c1ff72]/15 text-[#c1ff72]" : "bg-white/5 text-[#A1A1AA]"}`} data-testid="push-status-badge">{s.registered ? "Dieses Gerät: aktiv" : "Dieses Gerät: inaktiv"}</span>
      </div>
      <p className="text-xs text-[#A1A1AA]">Bei neuen Buchungsanfragen und Terminänderungen – ohne Kundennamen. Antippen öffnet die Anfrage; bestätigt wird immer erst durch dich.</p>
      <Checklist s={s} />
      <Button disabled={busy} onClick={toggle} className={`w-full h-11 rounded-none ${s.registered ? "bg-[#1A1A1A] hover:bg-[#1A1A1A] text-white border border-white/20" : "bg-[#c1ff72] hover:bg-[#a8e65c] text-black"}`} data-testid="push-toggle">{s.registered ? "Push-Benachrichtigungen deaktivieren" : "Push-Benachrichtigungen aktivieren"}</Button>
      <Button disabled={busy || !s.devices.length} onClick={test} variant="outline" className="w-full h-11 border-white/20 text-white rounded-none" data-testid="push-test"><Send className="w-4 h-4 mr-2" />Test-Push an mein iPhone senden</Button>
      <TestResult r={result} />
      <div className="space-y-1"><p className="text-xs text-white font-semibold">Registrierte Geräte</p><Devices list={s.devices} /></div>
      <Guide denied={s.perm === "denied"} />
    </div>
  );
}
